// Campaigns — demonstrate the naamconventie: market and type are parsed from the
// campaign name, so accounts that aren't split per market still slice correctly.
import { supabase } from "@/lib/supabase";
import type { Campaign, RegionFilter, BrandFilter, ActivityFilter, ChannelFilter } from "@/lib/api/types";
import { getBingPeriod } from './bing-period';

export interface CampaignQuery {
  region: RegionFilter;
  brand: BrandFilter;
  activity: ActivityFilter;
  channel: ChannelFilter;
  /** Free-text match against the campaign name (the naamconventie search). */
  search?: string;
  /** Structural segment from the naamconventie
   *  (e.g. "USA | Ecom | Sales | Outlets | Search | Branded"). */
  segment?: CampaignSegment;
  /** Selected reporting window (yyyy-mm-dd). Campaigns whose run dates don't
   *  overlap this window are excluded, so the period filter takes effect. */
  from?: string;
  to?: string;
  nativeBing?: boolean;
}

/** Structural segments (Victor's taxonomy v1). Matched against lowercased
 *  "|" name segments first, then type_tag/activity as fallback. */
export type CampaignSegment =
  | "all" | "ecom" | "awa" | "sales" | "brand"
  | "outlets" | "switches" | "jobads" | "no-outlet-switch" | "search" | "shopping";

export const SEGMENT_ITEMS: Array<{ value: CampaignSegment; label: string }> = [
  { value: "all", label: "Alle segmenten" },
  { value: "search", label: "Search" },
  { value: "shopping", label: "Shopping" },
  { value: "ecom", label: "Ecom" },
  { value: "awa", label: "Awa" },
  { value: "sales", label: "Sales" },
  { value: "brand", label: "Brand" },
  { value: "outlets", label: "Outlets" },
  { value: "switches", label: "Switches" },
  { value: "jobads", label: "Job ads" },
  { value: "no-outlet-switch", label: "Alles excl. outlets & switches" },
];

function campaignSegments(c: Pick<Campaign, "name" | "type_tag" | "activity_type">): Set<string> {
  const found = new Set<string>();
  const segs = (c.name ?? "").toLowerCase().split(/[|–—\-]/).map((s) => s.trim());
  if (segs.includes("outlets")) found.add("outlets");
  if (segs.includes("switches")) found.add("switches");
  if (segs.includes("sales")) found.add("sales");
  if (segs.includes("brand")) found.add("brand");
  if (segs.includes("awa")) found.add("awa");
  if (segs.includes("search")) found.add("search");
  if (segs.includes("shopping")) found.add("shopping");
  const type = (c.type_tag ?? "").toLowerCase();
  if (type === "job" || c.activity_type === "job_ads" || /\bjob\b/.test(c.name ?? "")) found.add("jobads");
  if (type === "lighting" || c.activity_type === "awareness") found.add("awa");
  if (type === "ecom" || c.activity_type === "ecom") found.add("ecom");
  return found;
}

/** Test helper: does a campaign belong to a structural segment? */
export function matchesSegment(
  c: Pick<Campaign, "name" | "type_tag" | "activity_type">,
  segment: CampaignSegment,
): boolean {
  if (segment === "all") return true;
  const found = campaignSegments(c);
  if (segment === "no-outlet-switch") return !found.has("outlets") && !found.has("switches");
  return found.has(segment);
}

export async function getCampaigns(q: CampaignQuery): Promise<Campaign[]> {
  if (!q.from || !q.to) throw new Error("Kies een begin- en einddatum.");
  const { data, error } = await supabase.rpc("rpc_campaign_window", { p_from: q.from, p_to: q.to });
  if (error) throw error;
  let rows = (data ?? []) as Campaign[];
  if (q.nativeBing) {
    const report = await getBingPeriod(q.from, q.to);
    if (report.status !== 'ready') throw new Error('MICROSOFT_REPORT_PENDING');
    const {data: platform,error: platformError} = await supabase.from('platforms').select('id').eq('key','bing').single();
    if (platformError) throw platformError;
    rows = [...rows.filter(c => c.platform_id !== platform.id), ...(report.rows ?? [])];
  }
  return rows
    .filter(c => (q.region === "all" || c.region === q.region)
      && (q.brand === "all" || c.brand_id === q.brand)
      && (q.channel === "all" || c.platform_id === q.channel)
      && (q.activity === "all" || (q.activity === "non_ecom" ? c.activity_type !== "ecom" : c.activity_type === q.activity))
      && (!q.search?.trim() || c.name.toLowerCase().includes(q.search.trim().toLowerCase()))
      && matchesSegment(c, q.segment ?? "all"))
    .map(c => ({ ...c, spend:Number(c.spend), revenue:Number(c.revenue),
      impressions:Number(c.impressions),clicks:Number(c.clicks),
      conversions:Number(c.conversions),applications:Number(c.applications) }))
    .sort((a,b) => b.spend-a.spend);
}
